import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Box,
  Stack,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import ServiceTasksList from '../components/ServiceTasksList';
import PageLayout from '../components/PageLayout';
import JobChat from '../components/JobChat';

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/${bookingId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch booking details');
        }

        const data = await response.json();
        setBooking(data);

        // Fetch linked job (non-blocking)
        try {
          const jobRes = await fetch(`${process.env.REACT_APP_API_URL}/api/jobs/by-booking/${bookingId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (jobRes.ok) {
            const jobData = await jobRes.json();
            setJobId(jobData.id);
          }
        } catch (_) {}
      } catch (err) {
        setError('Failed to load booking details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'PPP');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(new Date(dateString), 'p');
    } catch (error) {
      console.error('Time formatting error:', error);
      return 'Invalid Time';
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      setCancelError('Please tell us why you are cancelling.');
      return;
    }
    setCancelling(true);
    setCancelError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/${bookingId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cancellationReason: cancelReason.trim() })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to cancel booking');
      }
      const updated = await response.json();
      setBooking(updated);
      setCancelDialogOpen(false);
    } catch (err) {
      setCancelError(err.message || 'Failed to cancel booking. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'success';
      case 'PENDING': return 'warning';
      case 'COMPLETED': return 'info';
      case 'CANCELLED': return 'error';
      case 'IN_PROGRESS': return 'primary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress size={60} />
          </Box>
        </Container>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </PageLayout>
    );
  }

  if (!booking) {
    return (
      <PageLayout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="info">Booking not found</Alert>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Booking Details
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/bookings')}
        >
          Back to Bookings
        </Button>
      </Stack>
      
      <Grid container spacing={3}>
        {/* Main Booking Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            {/* Status and ID */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Booking ID: {booking.id}
              </Typography>
              <Chip
                label={booking.status}
                color={getStatusColor(booking.status)}
                sx={{ fontWeight: 'bold' }}
              />
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Service Details */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Service Information
                </Typography>
                <Stack spacing={2}>
                  {/* Service Type */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Service Type
                    </Typography>
                    <Typography variant="body1">
                      {booking.service || 'Standard Cleaning'}
                    </Typography>
                  </Box>

                  {/* Date & Time */}
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon color="primary" fontSize="small" />
                      <Typography variant="body1">
                        {formatDate(booking.serviceDate)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimeIcon color="primary" fontSize="small" />
                      <Typography variant="body1">
                        {formatTime(booking.serviceTime)}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Location */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Service Location
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LocationIcon color="primary" fontSize="small" />
                      <Typography variant="body1">
                        {booking.address}<br />
                        {booking.city}, {booking.state} {booking.zipCode}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Amount & payment status */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Service Cost
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <PaymentIcon color="primary" fontSize="small" />
                      <Typography variant="body1" fontWeight="bold">
                        ${booking.totalAmount}
                      </Typography>
                      {booking.paymentStatus === 'REFUNDED' ? (
                        <Chip label="Refunded" color="warning" size="small" variant="outlined" />
                      ) : (booking.paymentStatus === 'RELEASED' || booking.paymentStatus === 'HELD') ? (
                        <Chip label="Paid" color="success" size="small" variant="outlined" />
                      ) : (
                        <Chip label="Unpaid" color="default" size="small" variant="outlined" />
                      )}
                    </Box>
                    {(() => {
                      const paid = (booking.payments || []).find(p => p.status === 'succeeded' && p.receiptUrl);
                      return paid ? (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {paid.cardBrand && paid.cardLast4 && (
                            <span style={{ textTransform: 'capitalize' }}>{paid.cardBrand} •••• {paid.cardLast4} — </span>
                          )}
                          <a href={paid.receiptUrl} target="_blank" rel="noopener noreferrer">View receipt</a>
                        </Typography>
                      ) : null;
                    })()}
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Service Provider
                </Typography>
                {booking.maid ? (
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="primary" fontSize="small" />
                      <Typography variant="body1">
                        {booking.maid.firstName} {booking.maid.lastName}
                      </Typography>
                    </Box>
                    {/* Add more provider details as needed */}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Service provider not yet assigned
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Special Instructions */}
            {booking.specialInstructions && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Special Instructions
                </Typography>
                <Typography variant="body1">
                  {booking.specialInstructions}
                </Typography>
              </Box>
            )}

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              {booking.status === 'PENDING' && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/bookings/${booking.id}/reschedule`)}
                >
                  Reschedule
                </Button>
              )}
              {['PENDING', 'CONFIRMED'].includes(booking.status) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => { setCancelError(null); setCancelDialogOpen(true); }}
                >
                  Cancel Booking
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Service Tasks */}
        {booking.status !== 'CANCELLED' && (
          <Grid item xs={12}>
            <ServiceTasksList bookingId={bookingId} />
          </Grid>
        )}

        {/* Messaging */}
        {jobId && (
          <Grid item xs={12}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <JobChat jobId={jobId} compact />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Cancel confirmation dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel this booking?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This can't be undone. Your cleaning on {formatDate(booking.serviceDate)} will be cancelled.
          </DialogContentText>
          {cancelError && (
            <Alert severity="error" sx={{ mb: 2 }}>{cancelError}</Alert>
          )}
          <TextField
            label="Reason for cancelling"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            required
            disabled={cancelling}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            Keep Booking
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelBooking}
            disabled={cancelling || !cancelReason.trim()}
            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
          >
            {cancelling ? 'Cancelling…' : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </PageLayout>
  );
};

export default BookingDetails;